import axios from 'axios';
import config from '../config/api';

const API_URL = config.endpoints.solicitudIngreso;

class SolicitudIngresoService {
  /**
   * Generar PDF de solicitud de ingreso para una socia
   * @param {string} sociaId - ID de la socia
   * @returns {Promise<Blob>} PDF generado
   */
  async generatePDF(sociaId) {
    try {
      const response = await axios.post(
        `${API_URL}/socia/${sociaId}/generate`,
        {},
        {
          responseType: 'blob', // Importante para recibir el PDF como blob
          headers: {
            'Accept': 'application/pdf'
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error al generar PDF de solicitud:', error);
      
      if (error.response?.status === 404) {
        throw new Error('No se encontró el formato de solicitud o la socia no existe');
      }
      
      throw new Error('Error al generar el PDF de solicitud de ingreso');
    }
  }

  /**
   * Descargar PDF de solicitud de ingreso
   * @param {string} sociaId - ID de la socia
   * @param {string} nombreSocia - Nombre de la socia para el archivo
   */
  async downloadPDF(sociaId, nombreSocia = 'socia') {
    try {
      const pdfBlob = await this.generatePDF(sociaId);
      
      // Crear URL temporal para el blob
      const url = window.URL.createObjectURL(pdfBlob);
      
      // Crear elemento <a> temporal para descargar
      const link = document.createElement('a');
      link.href = url;
      
      // Nombre del archivo: "Solicitud_Ingreso_NombreSocia.pdf"
      const fileName = `Solicitud_Ingreso_${nombreSocia.replace(/\s+/g, '_')}.pdf`;
      link.setAttribute('download', fileName);
      
      // Agregar al DOM, hacer clic y remover
      document.body.appendChild(link);
      link.click();
      
      // Limpiar
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('Error al descargar PDF:', error);
      throw error;
    }
  }

  /**
   * Obtener el formato actual de solicitud
   * @returns {Promise<Object>} Formato de solicitud
   */
  async getCurrentFormat() {
    try {
      const response = await axios.get(`${API_URL}/current`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener formato actual:', error);
      throw new Error('Error al obtener el formato de solicitud');
    }
  }

  /**
   * Create or update format from multipart FormData
   * Attempts POST, on 409 will PUT to existing id
   * @param {FormData} formData
   */
  async createOrUpdateFromForm(formData) {
    try {
      const res = await axios.post(`${API_URL}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return { success: true, data: res.data };
    } catch (error) {
      const status = error?.response?.status;
      if (status === 409) {
        try {
          const current = await this.getCurrentFormat();
          const id = current?.SOLICITUDINGRESO_ID;
          if (!id) return { success: false, error: 'NO_FORMAT_ID' };
          const putRes = await axios.put(`${API_URL}/${encodeURIComponent(id)}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          return { success: true, data: putRes.data };
        } catch (e) {
          console.error('Error updating solicitud format after conflict:', e);
          return { success: false, error: e?.response?.data || e?.message || 'ERROR' };
        }
      }
      console.error('Error creating solicitud format:', error);
      return { success: false, error: error?.response?.data || error?.message || 'ERROR' };
    }
  }
}

export default new SolicitudIngresoService();
